// frontend/src/components/payment/PaymentSkeletonParts.js
//
// The payment pages' skeleton shapes - drawn with the shared page-skeleton
// parts (components/loading/skeletonParts.js) inside the SAME card the pages
// render (PaymentShell), so the swap from skeleton to page moves nothing but
// the shapes. Used twice, as one continuous breathing skeleton (owner rule: a
// spinner never follows a skeleton):
//
//   1. the routes' chunk fallback - PaymentPageSkeleton (role="status" shell)
//   2. the success page's own pending state - medallion, tagline, receipt,
//      actions and reference as SkeletonShapes under the real "Confirming
//      your payment..." title and subtitle
//
// STRUCTURE PARITY: each skeleton draws every block its resolved page draws,
// in the same order, so resolving swaps shapes for text and moves nothing:
//   success (confirmed)  hero + tagline / receipt / actions / reference
//   failure              hero / notice + help / actions / help lead + address
//                        / reference
//                        (the unverified, no-retry failure draws no help
//                        sentence - its notice already promises the check;
//                        the skeleton, drawn before the outcome is known,
//                        follows the common retry case)
//
// Fill, pulse clock and motion gates come from the surrounding SkeletonStatus
// / SkeletonShapes, never from here. Line sizes are the real text's sizes
// (paymentBrand.js), so a line is the same box as the text it stands for.

import React from "react";
import { Box } from "@mui/material";

import { SkeletonBlock, SkeletonLine } from "../loading/skeletonParts";
import {
  BRAND,
  HELP_SX,
  NOTICE_SX,
  RECEIPT_HEADING_SX,
  RECEIPT_LIST_SX,
  RECEIPT_ROW_SX,
  RECEIPT_SECTION_SX,
  REFERENCE_SX,
  SUBTITLE_SX,
  TAGLINE_SX,
  TITLE_SX,
} from "./paymentBrand";

/** Rows the receipt placeholder draws - a typical confirmed receipt. */
export const RECEIPT_SKELETON_ROWS = 6;

/** The medallion's disc. */
export function MedallionSkeleton() {
  return (
    <Box sx={{ display: "flex", justifyContent: "center" }}>
      <SkeletonBlock width={{ xs: 76, sm: 88 }} height={{ xs: 76, sm: 88 }} radius="50%" />
    </Box>
  );
}

/**
 * The whole hero: medallion, title, subtitle (and a tagline line).
 *
 * @param {object} p
 * @param {boolean} [p.tagline=false]
 */
export function HeroSkeleton({ tagline = false }) {
  return (
    <>
      <MedallionSkeleton />
      <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
        <SkeletonLine
          width={{ xs: "62%", sm: "54%" }}
          fontSize={TITLE_SX.fontSize}
          lineHeight={TITLE_SX.lineHeight}
          sx={{ mt: TITLE_SX.mt }}
        />
        {/* The 2px hairline + its margin. */}
        <Box sx={{ height: "2px", mt: 1.75 }} />
        <SkeletonLine
          width={{ xs: "84%", sm: "68%" }}
          fontSize={SUBTITLE_SX.fontSize}
          lineHeight={SUBTITLE_SX.lineHeight}
          sx={{ mt: SUBTITLE_SX.mt }}
        />
        {tagline && <TaglineSkeleton />}
      </Box>
    </>
  );
}

/**
 * The confirmed hero's tagline line (TAGLINE_SX), centred. The wrapper spans
 * the full row (width 100%): inside HeroSkeleton's centred column a
 * content-sized wrapper would shrink to zero and the line's % width would
 * resolve against that zero.
 */
export function TaglineSkeleton() {
  return (
    <Box sx={{ width: "100%", display: "flex", justifyContent: "center" }} data-testid="payment-tagline-skeleton">
      <SkeletonLine
        width={{ xs: "74%", sm: "60%" }}
        fontSize={TAGLINE_SX.fontSize}
        lineHeight={TAGLINE_SX.lineHeight}
        sx={{ mt: TAGLINE_SX.mt }}
      />
    </Box>
  );
}

/** The order reference line under the actions (REFERENCE_SX), centred. */
export function ReferenceSkeleton() {
  return (
    <Box
      sx={{ display: "flex", justifyContent: "center", mt: REFERENCE_SX.mt }}
      data-testid="payment-reference-skeleton"
    >
      <SkeletonLine width="15em" fontSize={REFERENCE_SX.fontSize} lineHeight={REFERENCE_SX.lineHeight} />
    </Box>
  );
}

/**
 * The receipt sub-card: heading line, then label / value rows.
 *
 * @param {object} p
 * @param {number} [p.rows=RECEIPT_SKELETON_ROWS]
 */
export function ReceiptSkeleton({ rows = RECEIPT_SKELETON_ROWS }) {
  return (
    <Box sx={RECEIPT_SECTION_SX} data-testid="payment-receipt-skeleton">
      <Box sx={{ mb: RECEIPT_HEADING_SX.mb, px: RECEIPT_HEADING_SX.px }}>
        <SkeletonLine width="7.5em" fontSize={RECEIPT_HEADING_SX.fontSize} lineHeight={RECEIPT_HEADING_SX.lineHeight} />
      </Box>
      <Box sx={RECEIPT_LIST_SX}>
        {Array.from({ length: rows }, (_, i) => (
          <Box key={i} sx={RECEIPT_ROW_SX}>
            <SkeletonLine width="5.5em" fontSize="14px" lineHeight={1.45} />
            <SkeletonLine width={i === 0 ? "6em" : "7em"} fontSize="14px" lineHeight={1.45} />
          </Box>
        ))}
      </Box>
    </Box>
  );
}

// The failure message usually runs to two lines at the notice size (the
// longest, "unverified", to three on a phone) - two lines is the box the eye
// expects, sized by the text's own metrics rather than a fixed height.
const NOTICE_SKELETON_LINES = [{ xs: "92%", sm: "88%" }, { xs: "64%", sm: "52%" }];

/**
 * The failure page's message box (NOTICE_SX padding, radius and border box,
 * lines at the notice's type size) and the help line under it (HELP_SX).
 */
export function NoticeSkeleton() {
  return (
    <Box data-testid="payment-notice-skeleton">
      <Box
        sx={{
          px: NOTICE_SX.px,
          py: NOTICE_SX.py,
          borderRadius: NOTICE_SX.borderRadius,
          border: `1px solid ${BRAND.hairline}`,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
        }}
      >
        {NOTICE_SKELETON_LINES.map((width, i) => (
          <SkeletonLine key={i} width={width} fontSize={NOTICE_SX.fontSize} lineHeight={NOTICE_SX.lineHeight} />
        ))}
      </Box>
      <Box sx={{ display: "flex", justifyContent: "center", mt: HELP_SX.mt }}>
        <SkeletonLine width={{ xs: "86%", sm: "72%" }} fontSize={HELP_SX.fontSize} lineHeight={HELP_SX.lineHeight} />
      </Box>
    </Box>
  );
}

/**
 * The failure page's "Need further help?" line and the support address under
 * it (PaymentFailureView: a 13px line, then a 32px-tall link).
 */
export function HelpLeadSkeleton() {
  return (
    <Box
      sx={{ mt: 2, display: "flex", flexDirection: "column", alignItems: "center" }}
      data-testid="payment-help-lead-skeleton"
    >
      <SkeletonLine width="12em" fontSize="13px" lineHeight={1.5} />
      <Box sx={{ minHeight: 32, display: "flex", alignItems: "center" }}>
        <SkeletonLine width="11em" fontSize="13px" lineHeight={1.5} />
      </Box>
    </Box>
  );
}

/** The two action pills - stacked on phones, side by side from `sm`. */
export function ActionsSkeleton() {
  return (
    <Box sx={{ mt: 3, display: "flex", flexDirection: { xs: "column", sm: "row" }, gap: 1.25 }}>
      <SkeletonBlock width={{ xs: "100%", sm: "auto" }} height={48} radius="999px" sx={{ flex: { sm: "1 1 0" } }} />
      <SkeletonBlock width={{ xs: "100%", sm: "auto" }} height={48} radius="999px" sx={{ flex: { sm: "1 1 0" } }} />
    </Box>
  );
}
